import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ActionMenu, ConfirmDialog, Dialog, DialogError, Field } from '../../components/admin/AdminActions';
import { inputClass, primaryButton, secondaryButton } from '../../components/admin/AdminUI';
import { StatusBadge } from '../../components/StatusBadge';
import type { DirectoryUser } from '../../types/api';
import { readAdminToken } from '../../utils/sessionRecovery';
import { adminRequest, useAdminAction } from './adminRequest';

/** The signed-in admin's own user id (the token subject), for self-protection in the UI. */
export function currentAdminUserId(): string | null {
  const token = readAdminToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: string;
      userId?: string;
    };
    return payload.sub ?? payload.userId ?? null;
  } catch {
    return null;
  }
}

/** Status badges for one person: deactivated / temporary password / signed in now. */
export function UserStatusBadges({ user }: { user: DirectoryUser }) {
  return (
    <div className="flex flex-wrap gap-1">
      {user.deactivatedAt ? (
        <StatusBadge status="error" label="Deactivated" />
      ) : (user.activeSessions ?? 0) > 0 ? (
        <StatusBadge status="success" label="Signed in now" />
      ) : (
        <StatusBadge status="neutral" label="Active" />
      )}
      {user.mustChangePassword && !user.deactivatedAt && (
        <StatusBadge status="warning" label="Temporary password" />
      )}
    </div>
  );
}

type UserDialog = 'edit' | 'password' | 'deactivate' | 'reactivate' | 'verify' | 'grant' | 'revoke' | null;

/** The person's action menu and every dialog it opens. */
export function UserActions({
  user,
  onChanged,
  menuLabel,
}: {
  user: DirectoryUser;
  onChanged: () => void;
  menuLabel?: string;
}) {
  const [dialog, setDialog] = useState<UserDialog>(null);
  const self = currentAdminUserId() === user.id;
  const deactivated = !!user.deactivatedAt;
  const close = () => setDialog(null);
  const done = () => {
    close();
    onChanged();
  };

  // A span, not a fragment: dialogs render in a portal, and React still bubbles
  // their clicks to this tree — a table row's click-to-open must not see them.
  return (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
      <ActionMenu
        label={menuLabel ?? `Actions for ${user.email}`}
        items={[
          { label: 'Edit name or email', onSelect: () => setDialog('edit') },
          {
            label: 'Reset password…',
            onSelect: () => setDialog('password'),
            disabled: self || deactivated,
            hint: self ? 'Not for your own account' : 'Reactivate the account first',
          },
          { label: 'Mark email verified', onSelect: () => setDialog('verify'), hidden: user.isEmailVerified },
          {
            label: 'Make Central admin',
            onSelect: () => setDialog('grant'),
            hidden: user.isCentralAdmin,
          },
          {
            label: 'Remove Central admin',
            onSelect: () => setDialog('revoke'),
            hidden: !user.isCentralAdmin,
            disabled: self,
            hint: 'You cannot remove your own admin access',
            danger: true,
          },
          {
            label: 'Reactivate account',
            onSelect: () => setDialog('reactivate'),
            hidden: !deactivated,
          },
          {
            label: 'Deactivate account…',
            onSelect: () => setDialog('deactivate'),
            hidden: deactivated,
            disabled: self,
            hint: 'You cannot deactivate yourself',
            danger: true,
          },
        ]}
      />
      <EditUserDialog open={dialog === 'edit'} user={user} self={self} onClose={close} onDone={done} />
      <ResetPasswordDialog open={dialog === 'password'} user={user} onClose={close} onDone={onChanged} />
      <SimpleUserAction
        open={dialog === 'verify'}
        title="Mark email verified?"
        body={<p>{user.email} will be treated as a confirmed address.</p>}
        confirmLabel="Mark verified"
        request={() => adminRequest('POST', `/users/${user.id}/verify-email`)}
        success="Email marked verified"
        onClose={close}
        onDone={done}
      />
      <SimpleUserAction
        open={dialog === 'grant'}
        title="Make this person a Central admin?"
        body={
          <p>
            {user.email} will be able to see and change every user and workspace in Central. They get the
            access the next time they sign in.
          </p>
        }
        confirmLabel="Make admin"
        request={() => adminRequest('PUT', `/users/${user.id}/central-admin`, { isCentralAdmin: true })}
        success="Central admin access granted"
        onClose={close}
        onDone={done}
      />
      <SimpleUserAction
        open={dialog === 'revoke'}
        title="Remove Central admin access?"
        danger
        body={<p>{user.email} loses the admin console and is signed out everywhere.</p>}
        confirmLabel="Remove admin access"
        request={() => adminRequest('PUT', `/users/${user.id}/central-admin`, { isCentralAdmin: false })}
        success="Central admin access removed"
        onClose={close}
        onDone={done}
      />
      <SimpleUserAction
        open={dialog === 'reactivate'}
        title="Reactivate this account?"
        body={<p>{user.email} will be able to sign in again with their existing password.</p>}
        confirmLabel="Reactivate"
        request={() => adminRequest('POST', `/users/${user.id}/reactivate`)}
        success="Account reactivated"
        onClose={close}
        onDone={done}
      />
      <DeactivateDialog open={dialog === 'deactivate'} user={user} onClose={close} onDone={done} />
    </span>
  );
}

function SimpleUserAction({
  open,
  title,
  body,
  confirmLabel,
  danger,
  request,
  success,
  onClose,
  onDone,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  request: () => Promise<unknown>;
  success: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  useEffect(() => {
    if (open) action.reset();
  }, [open, action.reset]);
  return (
    <ConfirmDialog
      open={open}
      title={title}
      body={body}
      confirmLabel={confirmLabel}
      danger={danger}
      busy={action.busy}
      error={action.error}
      onClose={onClose}
      onConfirm={async () => {
        const ok = await action.run(request);
        if (ok !== undefined) {
          toast.success(success);
          onDone();
        }
      }}
    />
  );
}

function DeactivateDialog({ open, user, onClose, onDone }: { open: boolean; user: DirectoryUser; onClose: () => void; onDone: () => void }) {
  const action = useAdminAction();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setReason('');
      action.reset();
    }
  }, [open, action.reset]);
  return (
    <ConfirmDialog
      open={open}
      title="Deactivate this account?"
      danger
      body={
        <>
          <p>
            <span className="font-medium text-slate-900">{user.email}</span> will be signed out everywhere and
            cannot sign in, open an app or refresh a session until reactivated.
          </p>
          <p>Nothing is deleted. Their workspaces and memberships stay as they are.</p>
        </>
      }
      confirmLabel="Deactivate"
      busy={action.busy}
      error={action.error}
      onClose={onClose}
      onConfirm={async () => {
        const ok = await action.run(() =>
          adminRequest('POST', `/users/${user.id}/deactivate`, reason.trim() ? { reason: reason.trim() } : {}),
        );
        if (ok !== undefined) {
          toast.success('Account deactivated');
          onDone();
        }
      }}
    >
      <div className="mt-4">
        <Field label="Reason (optional, kept in the audit log)">
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className={inputClass} />
        </Field>
      </div>
    </ConfirmDialog>
  );
}

function EditUserDialog({
  open,
  user,
  self,
  onClose,
  onDone,
}: {
  open: boolean;
  user: DirectoryUser;
  self: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [name, setName] = useState(user.name ?? '');
  const [email, setEmail] = useState(user.email);
  useEffect(() => {
    if (open) {
      setName(user.name ?? '');
      setEmail(user.email);
      action.reset();
    }
  }, [open, user, action.reset]);
  const emailChanged = email.trim().toLowerCase() !== user.email.toLowerCase();
  const nameChanged = name.trim() !== (user.name ?? '');

  const save = async () => {
    const body: Record<string, string> = {};
    if (nameChanged) body['name'] = name.trim();
    if (emailChanged) body['email'] = email.trim();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    const ok = await action.run(() => adminRequest('PATCH', `/users/${user.id}`, body));
    if (ok !== undefined) {
      toast.success(emailChanged ? 'Saved. They were signed out and must use the new email.' : 'Saved');
      onDone();
    }
  };

  return (
    <Dialog
      open={open}
      title="Edit person"
      description={user.email}
      onClose={onClose}
      busy={action.busy}
      footer={
        <>
          <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
            Cancel
          </button>
          <button type="button" className={primaryButton} onClick={save} disabled={action.busy}>
            {action.busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={255} className={inputClass} />
        </Field>
        <Field
          label="Sign-in email"
          help={
            self
              ? 'You cannot change your own sign-in email here.'
              : emailChanged
                ? 'Changing it signs them out everywhere, marks the new address unverified and unlinks Google sign-in.'
                : undefined
          }
        >
          <input
            aria-label="Sign-in email"
            type="email"
            value={email}
            disabled={self}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={255}
            className={inputClass}
          />
        </Field>
      </div>
      <DialogError message={action.error} />
    </Dialog>
  );
}

/** Shows a generated temporary password once, with a copy button. */
function OneTimePassword({ password }: { password: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-2" data-testid="one-time-password">
      <p className="text-sm text-slate-600">
        Give them this temporary password now. It is shown <span className="font-semibold">only once</span>; they
        must choose their own the next time they sign in.
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 select-all rounded-lg bg-slate-900 px-3 py-2 font-mono text-base tracking-wide text-emerald-300">
          {password}
        </code>
        <button
          type="button"
          className={secondaryButton}
          onClick={() => {
            void navigator.clipboard?.writeText(password).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function ResetPasswordDialog({
  open,
  user,
  onClose,
  onDone,
}: {
  open: boolean;
  user: DirectoryUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const action = useAdminAction();
  const [mode, setMode] = useState<'temporary_password' | 'reset_link'>('temporary_password');
  const [own, setOwn] = useState(false);
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setMode('temporary_password');
      setOwn(false);
      setPassword('');
      setShown(null);
      setSentTo(null);
      setChangedSomething(false);
      action.reset();
    }
  }, [open, action.reset]);

  // The page refreshes only when the admin closes this dialog: refreshing
  // earlier would unmount it and lose a one-time password before it is read.
  const [changedSomething, setChangedSomething] = useState(false);
  const finish = () => {
    setShown(null);
    onClose();
    if (changedSomething) onDone();
  };

  const submit = async () => {
    const body: Record<string, string> = { mode };
    if (mode === 'temporary_password' && own) {
      if (password.length < 10) {
        action.setError('Use at least 10 characters.');
        return;
      }
      body['password'] = password;
    }
    const res = await action.run(() =>
      adminRequest<{ temporaryPassword?: string; resetLinkSent?: boolean }>('POST', `/users/${user.id}/password`, body),
    );
    if (!res) return;
    setChangedSomething(true);
    if (mode === 'reset_link') {
      if (res.resetLinkSent === false) {
        toast.error('Signed out, but the email could not be queued. Try again.');
        onClose();
        onDone();
        return;
      }
      setSentTo(user.email);
      return;
    }
    if (res.temporaryPassword) {
      setShown(res.temporaryPassword);
    } else {
      toast.success('Temporary password set. They must change it at next sign-in.');
      onClose();
      onDone();
    }
  };

  const doneView = shown !== null || sentTo !== null;
  return (
    <Dialog
      open={open}
      title={doneView ? 'Password reset' : 'Reset password'}
      description={user.email}
      onClose={doneView ? finish : onClose}
      busy={action.busy}
      footer={
        doneView ? (
          <button type="button" className={primaryButton} onClick={finish}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
              Cancel
            </button>
            <button type="button" className={primaryButton} onClick={submit} disabled={action.busy}>
              {action.busy ? 'Working…' : mode === 'reset_link' ? 'Send reset link' : 'Set temporary password'}
            </button>
          </>
        )
      }
    >
      {shown !== null ? (
        <OneTimePassword password={shown} />
      ) : sentTo !== null ? (
        <p className="text-sm text-slate-600">
          A reset link is on its way to <span className="font-medium text-slate-900">{sentTo}</span>. They were signed
          out everywhere; their current password still works until they use the link.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Either way they are signed out of every device and app now.</p>
          <fieldset className="space-y-2">
            <legend className="sr-only">How should they get back in?</legend>
            <label className="flex items-start gap-3 rounded-lg p-3 ring-1 ring-slate-200 has-[:checked]:ring-emerald-500">
              <input
                type="radio"
                name="reset-mode"
                checked={mode === 'temporary_password'}
                onChange={() => setMode('temporary_password')}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-slate-900">Temporary password</span>
                <span className="block text-xs text-slate-500">
                  You give it to them; they must choose a new one when they next sign in.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg p-3 ring-1 ring-slate-200 has-[:checked]:ring-emerald-500">
              <input
                type="radio"
                name="reset-mode"
                checked={mode === 'reset_link'}
                onChange={() => setMode('reset_link')}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-slate-900">Email a reset link</span>
                <span className="block text-xs text-slate-500">They set a new password from the email.</span>
              </span>
            </label>
          </fieldset>
          {mode === 'temporary_password' && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={own} onChange={(e) => setOwn(e.target.checked)} />
                Choose it myself (otherwise Central generates a strong one)
              </label>
              {own && (
                <input
                  aria-label="Temporary password"
                  type="text"
                  autoComplete="off"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="At least 10 characters"
                />
              )}
            </div>
          )}
        </div>
      )}
      <DialogError message={action.error} />
    </Dialog>
  );
}

export function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (userId: string) => void;
}) {
  const action = useAdminAction();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [verified, setVerified] = useState(true);
  const [mode, setMode] = useState<'temporary_password' | 'reset_link'>('temporary_password');
  const [shown, setShown] = useState<string | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setEmail('');
      setName('');
      setVerified(true);
      setMode('temporary_password');
      setShown(null);
      setCreatedId(null);
      action.reset();
    }
  }, [open, action.reset]);

  const submit = async () => {
    const res = await action.run(() =>
      adminRequest<{ user: DirectoryUser; temporaryPassword?: string; resetLinkSent?: boolean }>('POST', '/users', {
        email: email.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        isEmailVerified: verified,
        mode,
      }),
    );
    if (!res) return;
    setCreatedId(res.user.id);
    if (res.temporaryPassword) {
      setShown(res.temporaryPassword);
      return;
    }
    toast.success(res.resetLinkSent === false ? 'Created, but the email could not be queued' : 'Account created; reset link sent');
    onCreated(res.user.id);
  };

  return (
    <Dialog
      open={open}
      title={shown ? 'Account created' : 'Add a person'}
      description={shown ? email : 'Creates a Central account. Add them to workspaces afterwards.'}
      onClose={shown && createdId ? () => onCreated(createdId) : onClose}
      busy={action.busy}
      footer={
        shown && createdId ? (
          <button type="button" className={primaryButton} onClick={() => onCreated(createdId)}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className={secondaryButton} onClick={onClose} disabled={action.busy}>
              Cancel
            </button>
            <button type="button" className={primaryButton} onClick={submit} disabled={action.busy || !email.trim()}>
              {action.busy ? 'Creating…' : 'Create account'}
            </button>
          </>
        )
      }
    >
      {shown ? (
        <OneTimePassword password={shown} />
      ) : (
        <div className="space-y-4">
          <Field label="Email">
            <input aria-label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Name (optional)">
            <input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
            Mark the email as verified
          </label>
          <Field label="How they get in">
            <select
              aria-label="How they get in"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'temporary_password' | 'reset_link')}
              className={inputClass}
            >
              <option value="temporary_password">Temporary password (shown once to you)</option>
              <option value="reset_link">Email them a link to set a password</option>
            </select>
          </Field>
        </div>
      )}
      <DialogError message={action.error} />
    </Dialog>
  );
}
